import { useState, useMemo } from "react";
import {
  useReactTable,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
} from "@tanstack/react-table";
import { Table } from "reactstrap";
import { FaChevronRight, FaChevronDown } from "react-icons/fa6";
import buildTree from "./buildTree";

export default function SuperUserLicenseDataGrid({
  columnBands,
  rows,
  columns: rawColumns,
  tableColumnExtensions,
}) {
  const [expanded, setExpanded] = useState({});

  const treeData = useMemo(() => buildTree(rows), [rows]);

  const columns = useMemo(
    () =>
      rawColumns.map((col) => {
        const ext = tableColumnExtensions?.find((e) => e.columnName === col.name);
        return {
          id: col.name,
          accessorKey: col.name,
          header: col.title,
          cell: (props) => props.getValue(),
          size: ext?.width ?? 150,
        };
      }),
    [rawColumns, tableColumnExtensions]
  );

  const table = useReactTable({
    data: treeData,
    columns,
    state: { expanded },
    onExpandedChange: setExpanded,
    getSubRows: (row) => row.subRows,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowId: (row) => row.id,
  });

  const hasBands = columnBands && columnBands.length > 0;
  const bandedColumns = new Set();
  if (hasBands) {
    for (const band of columnBands) {
      for (const child of band.children) {
        bandedColumns.add(child.columnName);
      }
    }
  }

  const allHeaders = table.getHeaderGroups()[0]?.headers || [];

  return (
    <div className="card" style={{ overflowX: "auto" }}>
      <Table className="table-striped" style={{ width: "100%" }}>
        <thead>
          {hasBands && (
            <tr>
              {allHeaders.map((header) => {
                if (bandedColumns.has(header.column.id)) return null;
                return (
                  <th key={header.id} rowSpan={2} style={{ width: header.getSize() }}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                );
              })}
              {columnBands.map((band) => (
                <th key={band.title} colSpan={band.children.length} className="text-center">
                  {band.title}
                </th>
              ))}
            </tr>
          )}
          <tr>
            {allHeaders.map((header) => {
              if (hasBands && !bandedColumns.has(header.column.id)) return null;
              return (
                <th key={header.id} style={{ width: header.getSize() }}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id}>
              {row.getVisibleCells().map((cell) => {
                const isTreeColumn = cell.column.id === "title";
                return (
                  <td key={cell.id} className="align-content-center">
                    {isTreeColumn ? (
                      <div className="d-flex align-items-center" style={{ paddingLeft: `${row.depth * 20}px` }}>
                        {row.getCanExpand() ? (
                          <span
                            onClick={row.getToggleExpandedHandler()}
                            className="cursor-pointer me-1 flex-shrink-0"
                            style={{ width: "16px" }}
                          >
                            {row.getIsExpanded() ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />}
                          </span>
                        ) : (
                          <span style={{ width: "20px", flexShrink: 0 }} />
                        )}
                        <span className="text-truncate">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </span>
                      </div>
                    ) : (
                      flexRender(cell.column.columnDef.cell, cell.getContext())
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}
